import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Preloader from '../components/Preloader';
import {
  Box, TextField, Button, Typography, Alert,
  InputAdornment, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useTenant } from '../context/TenantContext';
import api from '../axios';

const DARK = '#05080F';
const LIME = '#00F1A7';
const LIME_BG = 'rgba(0,241,167,0.08)';

function decodeJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    ));
  } catch { return null; }
}

function Login({ onLogin }) {
  const tenant = useTenant();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);
    try {
      const { data } = await api.post('/login', { email, senha });
      if (data?.access_token) {
        const token = data.access_token;
        localStorage.setItem('token', token);
        if (typeof onLogin === 'function') onLogin(token);
        const payload = decodeJwt(token);
        const tipo = payload?.tipo;
        if (tipo === 'aluno') return navigate('/area-aluno', { replace: true });
        return navigate('/home', { replace: true });
      }
      setErro('Usuário ou senha inválidos');
    } catch (err) {
      const msg = errorMsg(err) || 'Erro ao conectar ao servidor';
      setErro(msg);
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await api.post('/auth/esqueci-senha', { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao enviar email');
    }
    setForgotLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', position: 'relative', overflow: 'hidden', bgcolor: '#05080F' }}>
      {/* Hero banner (vídeo) ao fundo */}
      <Box component="video" autoPlay muted loop playsInline
        src={process.env.PUBLIC_URL + '/conectachurch-landing/hero.mp4'}
        poster={process.env.PUBLIC_URL + '/conectachurch-landing/dashboard-backdrop.jpg'}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.30, zIndex: 0 }} />
      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(150deg, rgba(5,8,15,0.92) 0%, rgba(5,8,15,0.80) 100%)', zIndex: 1 }} />

      {/* Painel esquerdo — branding (desktop only) */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        width: '45%',
        bgcolor: 'rgba(5,8,15,0.55)',
        zIndex: 2,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 6,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decoração circular */}
        <Box sx={{ position: 'absolute', width: 420, height: 420, bgcolor: LIME_BG, borderRadius: '50%', top: -120, right: -120 }} />
        <Box sx={{ position: 'absolute', width: 260, height: 260, bgcolor: LIME_BG, borderRadius: '50%', bottom: 30, left: -80 }} />

        <img
          src={process.env.PUBLIC_URL + '/images/logo_conecta_church_white_v2.svg'}
          alt="ConectaChurch"
          style={{ height: 78, width: 'auto', marginBottom: 40, position: 'relative' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <Typography variant="h3" sx={{ color: '#fff', textAlign: 'center', mb: 2, lineHeight: 1.3, position: 'relative' }}>
          Sistema Integrado<br />de Gestão
        </Typography>
        <Typography variant="body1" sx={{ color: '#888', textAlign: 'center', maxWidth: 300, lineHeight: 1.8, position: 'relative' }}>
          Consolidação, Start e Secretaria em uma única plataforma para sua comunidade.
        </Typography>

        {/* Fluxo visual */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 6, alignItems: 'center', position: 'relative' }}>
          {[
            { label: 'Consolidação', bg: '#555', color: '#fff' },
            { label: '→', arrow: true },
            { label: 'Start', bg: LIME, color: DARK },
            { label: '→', arrow: true },
            { label: 'Secretaria', bg: '#05080F', color: '#fff' },
          ].map((item, i) => item.arrow ? (
            <Typography key={i} sx={{ color: '#444', fontSize: 16, fontWeight: 700 }}>→</Typography>
          ) : (
            <Box key={i} sx={{
              bgcolor: item.bg, color: item.color,
              px: 1.5, py: 0.6, borderRadius: 1,
              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            }}>
              {item.label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Painel direito — formulário */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, md: 6 },
        bgcolor: { xs: '#05080F', md: 'transparent' },
      }}>
        {/* Logo mobile */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, textAlign: 'center' }}>
          <img
            src={process.env.PUBLIC_URL + '/logotipo-startfy-white.png'}
            alt="ConectaChurch"
            style={{ height: 44 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Box>

        <Box sx={{
          width: '100%', maxWidth: 400,
          bgcolor: 'rgba(10,20,28,0.88)',
          borderRadius: 1,
          p: { xs: 3, md: 5 },
          boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
          border: '1px solid rgba(0,241,167,0.22)',
          zIndex: 2,
        }}>
          <Typography variant="h4" sx={{ mb: 0.5, color: '#E9F1EF' }}>Bem-vindo</Typography>
          <Typography variant="body2" sx={{ mb: 3.5, color: '#93A7A3' }}>
            Acesse com seu e-mail e senha
          </Typography>

          {tenant && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Acessando</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#00F1A7' }}>{tenant.nome}</Typography>
            </Box>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: '#93A7A3', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Senha"
              type={showPass ? 'text' : 'password'}
              value={senha}
              onChange={e => setSenha(e.target.value)}
              required
              sx={{ mb: erro ? 2 : 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#93A7A3', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass(v => !v)} edge="end" size="small" tabIndex={-1}>
                      {showPass
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18, color: '#93A7A3' }} />
                        : <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#93A7A3' }} />
                      }
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {erro && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 1, fontSize: 13 }}>{erro}</Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ py: 1.4, fontSize: '0.95rem', fontWeight: 700 }}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: DARK }} /> : null}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
            <Typography
              sx={{ mt: 2, textAlign: 'center', cursor: 'pointer', color: { xs: 'rgba(255,255,255,0.5)', md: '#00F1A7' }, fontSize: '0.85rem', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => setShowForgot(true)}
            >
              Esqueci minha senha
            </Typography>
            {tenant && (
              <Typography
                sx={{ mt: 1.5, textAlign: 'center', cursor: 'pointer', color: { xs: 'rgba(255,255,255,0.8)', md: '#00F1A7' }, fontSize: '0.87rem', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                onClick={() => navigate(`/t/${tenant.slug}/cadastro`)}
              >
                Cadastre-se nesta igreja
              </Typography>
            )}
          </Box>
        </Box>

        <Typography variant="caption" sx={{ mt: 4, color: { xs: 'rgba(255,255,255,0.5)', md: '#ccc' } }}>
          ConectaChurch © {new Date().getFullYear()} — Todos os direitos reservados
        </Typography>
      </Box>

      {/* Dialog Esqueci Senha */}
      <Dialog open={showForgot} onClose={() => { setShowForgot(false); setForgotSent(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Recuperar senha</DialogTitle>
        <DialogContent>
          {forgotSent ? (
            <Typography sx={{ py: 2, color: 'success.main' }}>Email enviado! Verifique sua caixa de entrada.</Typography>
          ) : (
            <TextField
              autoFocus
              fullWidth
              label="Seu email"
              type="email"
              value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowForgot(false); setForgotSent(false); }}>Fechar</Button>
          {!forgotSent && (
            <Button variant="contained" onClick={handleForgotPassword} disabled={forgotLoading}>
              {forgotLoading ? <CircularProgress size={18} /> : 'Enviar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function LoginPageWrapper(props) {
  const [showPreloader, setShowPreloader] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setShowPreloader(false), 3000);
    return () => clearTimeout(timer);
  }, []);
  return showPreloader ? <Preloader /> : <Login {...props} />;
}
